import * as React from 'react'
import { Archive, HelpCircle, ShieldAlert } from 'lucide-react'

import {
  useDomainHistory,
  type DomainHistoryAggregate, type EnrichmentSignal, type WaybackPayload,
} from '@/lib/queries'
import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { LoadingState } from '@/components/ui/states'
import { formatDateTime, formatNumber } from '@/lib/utils'

/**
 * 域名的历史与外部信号。
 *
 * 这里的每个数字都必须带来源、时间和置信度，而且**未知不显示为 0**（§7.6）。
 * 「这个域名从没掉过」和「我们没有它的历史」是两个完全不同的判断，显示成 0 会把
 * 后者说成前者——而后者才是常态，因为平台只知道它监控过的那段。
 */
export function HistoryPanel({ domainId }: { domainId: string }) {
  const { data, isLoading } = useDomainHistory(domainId)

  if (isLoading) return <LoadingState rows={3} />
  if (!data) return null

  const wayback = data.signals.find((s) => s.kind === 'wayback')
  const gfw = data.signals.find((s) => s.kind === 'gfw')

  return (
    <div className="space-y-4 py-2">
      <HistorySection history={data.history} />
      <WaybackSection signal={wayback} enabled={data.sources.wayback} />
      <GFWSection signal={gfw} enabled={data.sources.gfw} />
    </div>
  )
}

/** 一个带来源标注的数值。未知就是未知，不填 0。 */
function Figure({
  label, value, unit, hint,
}: { label: string; value?: number | null; unit?: string; hint?: string }) {
  return (
    <div>
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {hint && (
          <Tooltip content={hint}>
            <HelpCircle className="h-3 w-3 cursor-help" aria-hidden />
          </Tooltip>
        )}
      </p>
      {value == null ? (
        <p className="mt-0.5 text-sm text-muted-foreground">未知</p>
      ) : (
        <p className="mt-0.5 text-sm font-medium tabular-nums">
          {formatNumber(value)}{unit && <span className="ml-0.5 text-xs font-normal">{unit}</span>}
        </p>
      )}
    </div>
  )
}

/** 来源、时间、置信度——三样缺一不可。 */
function Provenance({
  source, asOf, confidence, note,
}: { source: string; asOf?: string; confidence: number; note?: string }) {
  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.7rem] text-muted-foreground">
      <span>来源：{source}</span>
      {asOf && <span>· 采集于 {formatDateTime(asOf)}</span>}
      <span>· 置信度 {confidence}</span>
      {note && <span className="w-full">{note}</span>}
    </p>
  )
}

function HistorySection({ history }: { history: DomainHistoryAggregate | null }) {
  if (!history) {
    return (
      <Section title="注册历史">
        <p className="text-xs text-muted-foreground">
          还没有推导过。域名有生命周期事件之后，后台会自动汇总。
        </p>
      </Section>
    )
  }

  const derived = history.method === 'events'
  return (
    <Section title="注册历史">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure label="注册次数" value={history.registration_count} unit="次"
          hint="平台观测到的「被注册」事件次数" />
        <Figure label="掉落次数" value={history.drop_count} unit="次"
          hint="平台确认过的「变为可注册」次数" />
        <Figure label="累计注册时长" value={history.total_registered_days} unit="天" />
        <Figure label="本轮已注册" value={history.last_registration_duration_days} unit="天" />
      </div>
      {(history.first_registered_at || history.last_registered_at) && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          {history.first_registered_at && (
            <div>
              <p className="text-muted-foreground">最早观测到注册</p>
              <p className="mt-0.5">{formatDateTime(history.first_registered_at)}</p>
            </div>
          )}
          {history.last_registered_at && (
            <div>
              <p className="text-muted-foreground">最近一次注册</p>
              <p className="mt-0.5">{formatDateTime(history.last_registered_at)}</p>
            </div>
          )}
        </div>
      )}
      <Provenance
        source={derived ? '平台生命周期事件' : history.method}
        asOf={history.computed_at}
        confidence={history.confidence}
        note={derived
          ? '这些计数从平台开始监控该域名之日起算，是下界而非全量——它在被收录之前的历史，平台无从得知。'
          : undefined}
      />
    </Section>
  )
}

function WaybackSection({ signal, enabled }: { signal?: EnrichmentSignal; enabled?: boolean }) {
  if (!enabled) {
    return (
      <Section title="网页存档（Wayback）" icon={Archive}>
        <p className="text-xs text-muted-foreground">
          该数据源未启用。启用前需先确认 Internet Archive 的使用条款——它是捐助运营的公共服务，
          批量商用有额外要求。
        </p>
      </Section>
    )
  }
  if (!signal) {
    return (
      <Section title="网页存档（Wayback）" icon={Archive}>
        <p className="text-xs text-muted-foreground">尚未采集。后台会按批次补齐。</p>
      </Section>
    )
  }

  const p = signal.payload as WaybackPayload
  return (
    <Section title="网页存档（Wayback）" icon={Archive}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div>
          <p className="text-xs text-muted-foreground">存档快照</p>
          <p className="mt-0.5 text-sm font-medium tabular-nums">
            {p.truncated ? '≥ ' : ''}{formatNumber(p.capture_count)}
            {p.truncated && (
              <Tooltip content="查询达到上限，实际数量可能更多。这里显示的是下界。">
                <Badge variant="muted" className="ml-1 cursor-help">已截断</Badge>
              </Tooltip>
            )}
          </p>
        </div>
        {p.first_capture_at && (
          <div>
            <p className="text-xs text-muted-foreground">最早快照</p>
            <p className="mt-0.5 text-sm">{formatDateTime(p.first_capture_at)}</p>
          </div>
        )}
        {p.last_capture_at && (
          <div>
            <p className="text-xs text-muted-foreground">最近快照</p>
            <p className="mt-0.5 text-sm">{formatDateTime(p.last_capture_at)}</p>
          </div>
        )}
      </div>
      {p.status_summary && Object.keys(p.status_summary).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {Object.entries(p.status_summary).map(([code, n]) => (
            <Badge key={code} variant={code.startsWith('2') ? 'secondary' : 'muted'}>
              HTTP {code} × {formatNumber(n)}
            </Badge>
          ))}
        </div>
      )}
      <Provenance
        source={signal.source} asOf={signal.as_of} confidence={signal.confidence}
        note="存档覆盖本身就不均匀——抓到什么算什么，所以快照数不等于站点质量，更不等于 SEO 价值。"
      />
    </Section>
  )
}

function GFWSection({ signal, enabled }: { signal?: EnrichmentSignal; enabled?: boolean }) {
  if (!enabled) {
    return (
      <Section title="GFW 风险" icon={ShieldAlert}>
        <p className="text-xs text-muted-foreground">该检测未启用。</p>
      </Section>
    )
  }
  if (!signal) {
    return (
      <Section title="GFW 风险" icon={ShieldAlert}>
        <p className="text-xs text-muted-foreground">尚未探测。</p>
      </Section>
    )
  }
  return <GFWDetail signal={signal} />
}

const GFW_STATUS: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'muted' }> = {
  clear: { label: '未见异常', variant: 'success' },
  suspected: { label: '疑似受影响', variant: 'warning' },
  polluted: { label: '疑似被污染', variant: 'danger' },
  unknown: { label: '无法判定', variant: 'muted' },
}

/**
 * GFW 探测结论。
 *
 * 展示上有两条硬要求（§18.7）：必须写明这**不等于**法律意义上的「被墙」认定；
 * 必须把判定依据摊开——用了几个节点、两侧看到了什么、为什么得出这个结论。
 * 只给一个红色标签而不给理由，用户既没法信也没法反驳。
 */
function GFWDetail({ signal }: { signal: EnrichmentSignal }) {
  const v = signal.payload as {
    status: string
    confidence: number
    reason: string
    nodes_inside: number
    nodes_outside: number
    nodes_usable: number
    rounds: number
    random_subdomain_answered: boolean
    test_version: string
  }
  const meta = GFW_STATUS[v.status] ?? GFW_STATUS.unknown

  return (
    <Section title="GFW 风险" icon={ShieldAlert}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <span className="text-xs text-muted-foreground">置信度 {v.confidence}</span>
        {v.random_subdomain_answered && (
          <Tooltip content="向一个不可能存在的随机子域发起查询，却收到了应答。这只能由「有东西在伪造应答」解释，站点自身故障不会产生这种结果。">
            <Badge variant="danger" className="cursor-help">检测到 DNS 注入</Badge>
          </Tooltip>
        )}
      </div>

      <p className="mt-2 text-xs">{v.reason}</p>

      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div>
          <p className="text-muted-foreground">境内可用节点</p>
          <p className="mt-0.5 tabular-nums">{v.nodes_inside}</p>
        </div>
        <div>
          <p className="text-muted-foreground">境外可用节点</p>
          <p className="mt-0.5 tabular-nums">{v.nodes_outside}</p>
        </div>
        <div>
          <p className="text-muted-foreground">重复轮次</p>
          <p className="mt-0.5 tabular-nums">{v.rounds}</p>
        </div>
      </div>

      <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[0.7rem]">
        此结论仅为网络可达性观测，<strong>不等于法律意义上的「被封锁」认定</strong>。
        CDN 区域节点、临时故障、运营商劫持都可能产生相似特征。如结果存疑，可要求复测。
      </p>

      <Provenance
        source={`${signal.source}（方法版本 ${v.test_version}）`}
        asOf={signal.as_of}
        confidence={signal.confidence}
      />
    </Section>
  )
}

function Section({
  title, icon: Icon, children,
}: { title: string; icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-medium">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
        {title}
      </h4>
      {children}
    </div>
  )
}
